/**
 * Type-safe query keys for React Query
 * This provides a centralized, type-safe way to define and use query keys
 */

export const queryKeys = {
  // User data
  user: {
    me: () => ['user'] as const,
  },

  // Organizations
  organizations: {
    all: () => ['organizations'] as const,
    detail: (organizationId: string) =>
      ['organizations', organizationId] as const,
  },

  // API Keys
  apiKeys: {
    all: (organizationId: string) => ['apiKeys', organizationId] as const,
    detail: (organizationId: string, apiKeyId: string) =>
      ['apiKeys', organizationId, 'detail', apiKeyId] as const,
  },

  // Webhooks
  webhooks: {
    all: (organizationId: string) => ['webhooks', organizationId] as const,
    detail: (organizationId: string, webhookId: string) =>
      ['webhooks', organizationId, 'detail', webhookId] as const,
  },

  // Members
  members: {
    all: (organizationId: string) => ['members', organizationId] as const,
    detail: (organizationId: string, memberId: string) =>
      ['members', organizationId, 'detail', memberId] as const,
  },

  // Invitations
  invitations: {
    all: (organizationId: string) => ['invitations', organizationId] as const,
    detail: (organizationId: string, invitationId: string) =>
      ['invitations', organizationId, 'detail', invitationId] as const,
  },
}

// Export types directly from the literal return types
export type UserQueryKey = ReturnType<typeof queryKeys.user.me>
export type OrganizationsQueryKey = ReturnType<
  typeof queryKeys.organizations.all
>
export type ApiKeysQueryKey = ReturnType<typeof queryKeys.apiKeys.all>
export type WebhooksQueryKey = ReturnType<typeof queryKeys.webhooks.all>
export type MembersQueryKey = ReturnType<typeof queryKeys.members.all>
export type InvitationsQueryKey = ReturnType<typeof queryKeys.invitations.all>
